import { useEffect, type ReactNode } from "react";
import { Button } from "@/deps/ui-library";

/**
 * The print layer: a document as it will come out of the printer, over the screen it was opened
 * from.
 *
 * **Only the document prints.** The rules below hide everything else — the shell, the overlay's own
 * buttons — because an invoice with a navigation bar down the side of it is not a document.
 */
export function PrintOverlay({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** For the browser's own print dialog and the accessible name of the overlay. */
  title: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;

    // Escape closes it, as it does the modal dialogs. A print preview that can only be dismissed
    // with the mouse is one somebody gets stuck in.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-print-overlay=""
      className="bg-canvas fixed inset-0 z-modal overflow-auto"
    >
      {/* Hides the application behind the overlay while printing, and the overlay's own chrome.
          Scoped by the data attributes so nothing here depends on where it is mounted. */}
      <style>{`
        @media print {
          body > *:not([data-print-overlay]) { display: none !important; }
          [data-print-chrome] { display: none !important; }
          [data-print-overlay] { position: static !important; overflow: visible !important; }
          [data-print-sheet] { box-shadow: none !important; margin: 0 !important; }
          @page { margin: 16mm; }
        }
      `}</style>

      <div
        data-print-chrome=""
        className="border-border bg-card sticky top-0 flex items-center gap-2 border-b px-4 py-2"
      >
        <span className="text-foreground flex-1 text-sm font-medium">{title}</span>
        <Button colorScheme="default" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button colorScheme="primary" variant="solid" size="sm" onClick={() => globalThis.print()}>
          Print
        </Button>
      </div>

      <div className="mx-auto my-6 w-[210mm] max-w-full px-4">
        <div
          data-print-sheet=""
          className="bg-card border-border text-foreground relative border p-10 shadow-sm"
        >
          <div className="relative">{children}</div>
        </div>
      </div>
    </div>
  );
}
